import * as yup from 'yup';
import { ArrowLeft, Bell, Check } from 'react-feather';
import { useContext, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { CheckoutCard } from './CheckoutPage';
import { EmbedContext } from '../Common/EmbedProvider';
import { Event } from './helpers';
import { emailRegex, getServerErrors } from '../../common/helpers';
import { useEvent } from '../Common/EventProvider';
import EmailInput from '../../common/ParticipantAttributes/Attributes/EmailInput';
import Layout, { SectionTitle, TitleBar } from '../Common/Layout';
import SaveInterestMutation from './SaveInterestMutation';
import UI from '../../common/UI';
import useMutation from '../../api/useMutation';
import useParticipantExistenceCheck from '../../common/useParticipantExistenceCheck';
import useProject from '../Common/useProject';
import useValidation from '../../common/useValidation';

const InterestPage = () => {
  const { t } = useTranslation();
  const { embedded } = useContext(EmbedContext);
  const { event } = useEvent();
  const project = useProject();

  const [success, setSuccess] = useState(false);

  const intro = event.description;
  const enabled = event.enable_interest_list && event.interest_list;

  return (
    <Layout event={{ ...event, project }} embedded={embedded}>
      {intro && (
        <>
          <UI.HR />
          <UI.Div sc={{ color: 'gray.800' }}>
            <UI.HTML html={intro} />
          </UI.Div>
        </>
      )}

      <UI.Div>
        <TitleBar>
          <UI.Div>
            <UI.FadeIn>
              <UI.Link to={`/${event.id}`} title={t('back')}>
                <UI.Icon>
                  <ArrowLeft />
                </UI.Icon>
              </UI.Link>
            </UI.FadeIn>
          </UI.Div>
          <UI.Div>
            {t('interest_list.receive_updates')}
          </UI.Div>
          <UI.Div />
        </TitleBar>

        <CheckoutCard sc={{ hasPanels: true, active: true }}>
          {success && (
            <UI.FadeIn>
              <UI.FormGrid sc={{ textAlign: 'center' }}>
                <UI.Badge sc={{ size: 48, brand: 'success' }} style={{ margin: '0 auto' }}>
                  <UI.Icon sc={{ size: 2.5 }}>
                    <Check />
                  </UI.Icon>
                </UI.Badge>

                <SectionTitle>
                  {t('interest_list.subscribe_success')}
                </SectionTitle>

                <UI.Div>
                  <UI.LinkButton to={`/${event.id}`} sc={{ outline: true }}>
                    <UI.Icon>
                      <ArrowLeft />
                    </UI.Icon>
                    {' '}
                    {t('back')}
                  </UI.LinkButton>
                </UI.Div>
              </UI.FormGrid>
            </UI.FadeIn>
          )}
          {!success && (
            <UI.FadeIn>
              {enabled && <InterestListForm event={event} onSuccess={() => setSuccess(true)} />}
              {!enabled && (
                <UI.Div sc={{ textAlign: 'center' }}>
                  {t('interest_list.disabled_message')}
                </UI.Div>
              )}
            </UI.FadeIn>
          )}
        </CheckoutCard>
      </UI.Div>
    </Layout>
  );
};

interface InterestListFormProps {
  event: Event;
  onSuccess: () => unknown;
}

const InterestListForm = ({ event, onSuccess }: InterestListFormProps) => {
  const { t } = useTranslation();

  const form = useForm({
    values: {
      email: '',
      first_name: '',
      last_name: '',
    },
  });

  const interestList = event.interest_list;

  const { participantExists, loading: checkingParticipant } = useParticipantExistenceCheck({
    participant: form.values,
  });

  const schema = yup.object().shape({
    email: yup.string().required().matches(emailRegex, { excludeEmptyString: true, name: 'email' }),
    first_name: participantExists ? yup.mixed().nullable() : yup.string().required(),
    last_name: participantExists ? yup.mixed().nullable() : yup.string().required(),
  });

  const [subscribe, { error, loading }] = useMutation(SaveInterestMutation, {
    variables: {
      input: {
        interest_list: interestList.id,
        ...form.values,
      },
    },
    onCompleted: () => {
      onSuccess();
    },
  });

  const { valid, errors } = useValidation({
    schema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => subscribe()}>
      <UI.FormGrid>
        <UI.Div>
          <SectionTitle sc={{ mb: 2 }}>
            {interestList.title}
          </SectionTitle>

          <UI.Paragraph sc={{ mb: 0 }}>
            {interestList.label}
          </UI.Paragraph>
        </UI.Div>

        <EmailInput
          value={form.values}
          onChange={(value) => form.set((contact) => ({ ...contact, ...value }))}
          onBlur={(key) => form.setTouched(key, true)}
          touched={form.touched}
          errors={errors}
          required
        />

        {!participantExists && (
          <UI.FormGrid sc={{ columns: ['1fr 1fr'] }}>
            <UI.InputGroup sc={{ valid: !errors.first_name, touched: form.touched.first_name }}>
              <UI.InputLabel htmlFor="FirstName">
                {t('common:participant_attributes.first_name')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.DebouncedInput {...form.text('first_name')} maxLength={100} id="FirstName" />
              <UI.ErrorMessages
                attribute={t('common:participant_attributes.first_name')}
                errors={errors.first_name}
              />
            </UI.InputGroup>
            <UI.InputGroup sc={{ valid: !errors.last_name, touched: form.touched.last_name }}>
              <UI.InputLabel htmlFor="LastName">
                {t('common:participant_attributes.last_name')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.DebouncedInput {...form.text('last_name')} maxLength={100} id="LastName" />
              <UI.ErrorMessages
                attribute={t('common:participant_attributes.last_name')}
                errors={errors.last_name}
              />
            </UI.InputGroup>
          </UI.FormGrid>
        )}

        <UI.Button
          type="submit"
          sc={{ brand: 'secondary', block: true, loading }}
          disabled={loading || !valid || checkingParticipant}
        >
          <UI.Icon>
            <Bell />
          </UI.Icon>
          {' '}
          {t('subscribe')}
        </UI.Button>
      </UI.FormGrid>
    </UI.Form>
  );
};

export default InterestPage;
